import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, Messaging } from 'firebase/messaging';
import { Observable } from 'rxjs';
import { AppConfig, UserProfileService } from 'sigv-library';
import { EnvironmentService } from './environment.service';
import { SystemEnvService } from './system-env.service';



@Injectable({
  providedIn: 'root'
})
export class PushNotificationService {
  readonly authUrl: string = '/';
  private appType: AppType = 0;
  private appVersion: string | null = "";
  private messaging: Messaging | undefined;
  private firebaseConfig = EnvironmentService.environmentConfig.firebaseConfig;

  private vapidKey: string = EnvironmentService.environmentConfig.vapidKey;

  constructor(
    private userProfileService: UserProfileService,
    private systemEnvService: SystemEnvService,
    protected appConfig: AppConfig,
    private http: HttpClient
  ) {
    this.detectAppType();
    this.authUrl = appConfig.getApiUrl('webSubmission');

    if (this.isPushSupported()) {
      const app = initializeApp(this.firebaseConfig);
      this.messaging = getMessaging(app);
    } else {
      console.warn('Firebase Messaging is not supported in this browser.');
    }

    
  }

  private isPushSupported(): boolean {
    return typeof Notification !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window;
  }

  private async init() {
    // 確保瀏覽器支援 Service Worker
    if ('serviceWorker' in navigator) {
      const existingRegistration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
      if (!existingRegistration) {
        navigator.serviceWorker
          .register('/firebase-messaging-sw.js')
          .then((registration) => {
            console.log('Service Worker registered with scope:', registration.scope);
          })
          .catch((error) => {
            console.error('Failed to register Service Worker:', error);
          });
      }
      else {
        console.log('Service Worker already registered:', existingRegistration.scope);
      }
    } else {
      console.warn('Service Worker is not supported in this browser.');
    }
  }

  get envConfig(): any {
    return AppConfig.envConfig;
  }


  async enablePushNotifications() {
    await this.init(); // 確保 Service Worker 註冊

    // 如果瀏覽器不支援 Notification，直接返回
    if (typeof Notification === 'undefined') {
      console.warn('Notifications are not supported in this browser.');
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.log('Notification permission denied');
      return;
    }

    // 確保 Firebase Messaging 可用，否則直接跳過
    if (!this.messaging) {
      console.warn('Firebase Messaging is not supported in this browser.');
      return;
    }

    try {
      // 等待 Service Worker 準備好
      const registration = await navigator.serviceWorker.ready;
      const token = await getToken(this.messaging, { vapidKey: this.vapidKey, serviceWorkerRegistration: registration });

      if (!token) {
        console.error('Failed to retrieve token.');
        return;
      }

      const systemCode = this.systemEnvService.systemInfo.systemCode;
      const userNo = this.userProfileService.currentUserValue?.userNo;
      const appType = this.getAppType();
      const appVersion = this.getAppVersion();

      const payload = {
        app_id: systemCode,
        login_id: userNo,
        user_no: userNo,
        create_user: userNo,
        push_key: token,
        app_type: appType,
        app_version: appVersion,
      };

      // 發送 token 到後端
      this.insertPushKey(payload).subscribe({
        next: (response) => console.log('Push key inserted successfully:', response),
        error: (error) => console.error('Failed to insert push key:', error),
      });

      console.log('Firebase Token:', token);
    } catch (error) {
      console.error('Error during push notification setup:', error);
    }
  }


  /**
    * 偵測裝置的類別及版本 ex.Web, Android, iOS
    *
    * @private
    * @memberof PushNotificationService
    */
  private detectAppType(): void {
    const userAgent = navigator.userAgent || navigator.vendor;

    // 判斷裝置類型
    if (/android/i.test(userAgent)) {
      this.appType = AppType.Android;
      this.appVersion = this.extractVersion(userAgent, 'Android');
    } else if (/iPad|iPhone|iPod/.test(userAgent)) {
      this.appType = AppType.iOS;
      this.appVersion = this.extractVersion(userAgent, 'OS');
    } else {
      this.appType = AppType.Web;
      this.appVersion = null; // Web 瀏覽器通常無法直接獲取版本
    }
  }


  /**
   * 偵測版本號
   *
   * @private
   * @param {string} userAgent
   * @param {string} keyword
   * @return {*}  {string}
   * @memberof PushNotificationService
   */
  private extractVersion(userAgent: string, keyword: string): string {
    const regex = new RegExp(`${keyword} ([0-9_.]+)`);
    const match = userAgent.match(regex);
    return match ? match[1].replace(/_/g, '.') : 'Unknown';
  }


  /**
   * 回傳裝置類別
   *
   * @return {*}  {AppType}
   * @memberof PushNotificationService
   */
  getAppType(): AppType {
    return this.appType;
  }


  /**
   * 回傳裝置版本
   *
   * @return {*}  {string}
   * @memberof PushNotificationService
   */
  getAppVersion(): string | null {
    return this.appVersion;
  }

  /**
   * 呼叫推播註冊API
   *
   * @param {*} payload
   * @return {*}  {Observable<any>}
   * @memberof PushNotificationService
   */
  insertPushKey(payload: any): Observable<any> {
    const url = `${this.authUrl}/Push/Insert`;
    return this.http.post(url, payload);
  }
}

export enum AppType {
  Android = 0,
  Web = 1,
  iOS = 2,
}
